
import React from 'react';
import styled from 'styled-components';

interface NewButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
}

const NewButton: React.FC<NewButtonProps> = ({ 
  children, 
  onClick, 
  disabled = false, 
  type = 'button',
  className 
}) => {
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!disabled && onClick) {
        onClick();
      }
    }
  };

  return (
    <StyledWrapper className={className}>
      <button 
        type={type} 
        onClick={onClick} 
        disabled={disabled}
        onKeyDown={handleKeyDown}
        aria-label={typeof children === 'string' ? children : 'Submit button'}
        tabIndex={0}
      >
        <span>{children}</span>
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 74 74" height={34} width={34} aria-hidden="true">
          <circle strokeWidth={3} stroke="black" r="35.5" cy={37} cx={37} />
          <path fill="black" d="M25 35.5C24.1716 35.5 23.5 36.1716 23.5 37C23.5 37.8284 24.1716 38.5 25 38.5V35.5ZM49.0607 38.0607C49.6464 37.4749 49.6464 36.5251 49.0607 35.9393L39.5147 26.3934C38.9289 25.8076 37.9792 25.8076 37.3934 26.3934C36.8076 26.9792 36.8076 27.9289 37.3934 28.5147L45.8787 37L37.3934 45.4853C36.8076 46.0711 36.8076 47.0208 37.3934 47.6066C37.9792 48.1924 38.9289 48.1924 39.5147 47.6066L49.0607 38.0607ZM25 38.5L48 38.5V35.5L25 35.5V38.5Z" />
        </svg>
      </button>
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  button {
    cursor: pointer;
    font-weight: 700;
    transition: all 0.2s;
    padding: 10px 20px;
    border-radius: 100px;
    background: #cfef00; /* Lime Green */
    color: black; /* Ensuring text visibility */
    border: 1px solid transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    min-height: 44px; /* Touch target accessibility */
    position: relative;
  }

  button:hover {
    background: #c4e201; /* Slightly darker lime green */
  }

  button:focus {
    outline: 2px solid #374151;
    outline-offset: 2px;
  }

  button:focus-visible {
    outline: 2px solid #374151;
    outline-offset: 2px;
  }

  button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  button:disabled:hover {
    background: #cfef00; /* Keep original color when disabled */
  }

  button > svg {
    width: 34px;
    height: 34px;
    margin-left: 10px;
    transition: transform 0.3s ease-in-out;
    flex-shrink: 0;
  }

  button:hover svg {
    transform: translateX(5px);
  }

  button:active {
    transform: scale(0.95);
  }

  button:disabled:hover svg {
    transform: none;
  }

  /* Responsive adjustments */
  @media (max-width: 640px) {
    button {
      padding: 8px 16px;
      font-size: 14px;
      min-height: 40px;
    }

    button > svg {
      width: 28px;
      height: 28px;
      margin-left: 8px;
    }
  }

  @media (max-width: 480px) {
    button {
      padding: 6px 12px;
      font-size: 13px;
      min-height: 36px;
    }

    button > svg {
      width: 24px;
      height: 24px;
      margin-left: 6px;
    }
  }

  /* Header size variant */
  &.header-size button {
    padding: 6px 12px;
    font-size: 13px;
    min-height: 36px;
  }

  &.header-size button > svg {
    width: 24px;
    height: 24px;
    margin-left: 6px;
  }

  /* Mobile header adjustments */
  @media (max-width: 640px) {
    &.header-size button {
      padding: 4px 8px;
      font-size: 12px;
      min-height: 32px;
    }

    &.header-size button > svg {
      width: 20px;
      height: 20px;
      margin-left: 4px;
    }
  }
`;

export default NewButton;
